const RELEASE_RELOAD_STATE_KEY = 'webadmin:release-drift-reload-state';
const RELEASE_RELOAD_QUERY_PARAM = '__release_reload';
const RELEASE_RELOAD_COOLDOWN_MS = 30_000;
const RELEASE_PROBE_MIN_INTERVAL_MS = 60_000;
const RELEASE_ID_HEADER = 'X-Release-ID';

let releaseGuardInitialized = false;
let lastProbeAt = 0;
let inflightProbe: Promise<void> | null = null;

interface ReleaseReloadState {
  at: number;
  fromHref: string;
  runtimeReleaseId: string;
}

function currentClientReleaseId(): string {
  return (import.meta.env.VITE_RELEASE_ID ?? '').trim();
}

function readReloadState(): ReleaseReloadState | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(RELEASE_RELOAD_STATE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<ReleaseReloadState>;
    if (
      typeof parsed.at !== 'number' ||
      typeof parsed.fromHref !== 'string' ||
      typeof parsed.runtimeReleaseId !== 'string'
    ) {
      return null;
    }
    return {
      at: parsed.at,
      fromHref: parsed.fromHref,
      runtimeReleaseId: parsed.runtimeReleaseId,
    };
  } catch {
    return null;
  }
}

function writeReloadState(state: ReleaseReloadState): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(RELEASE_RELOAD_STATE_KEY, JSON.stringify(state));
  } catch {
    // Ignore sessionStorage failures.
  }
}

function clearReloadState(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.removeItem(RELEASE_RELOAD_STATE_KEY);
  } catch {
    // Ignore sessionStorage failures.
  }
}

function shouldReloadForReleaseMismatch(
  now: number,
  previous: ReleaseReloadState | null,
  runtimeReleaseId: string,
  online: boolean,
  cooldownMs = RELEASE_RELOAD_COOLDOWN_MS,
): boolean {
  if (!online) {
    return false;
  }
  if (!previous) {
    return true;
  }
  if (previous.runtimeReleaseId !== runtimeReleaseId) {
    return true;
  }
  return now - previous.at >= cooldownMs;
}

function buildReleaseReloadHref(currentHref: string, now: number): string {
  const nextUrl = new URL(currentHref);
  nextUrl.searchParams.set(RELEASE_RELOAD_QUERY_PARAM, String(now));
  return nextUrl.toString();
}

function clearReleaseReloadQueryParam(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const currentUrl = new URL(window.location.href);
  if (!currentUrl.searchParams.has(RELEASE_RELOAD_QUERY_PARAM)) {
    return;
  }
  currentUrl.searchParams.delete(RELEASE_RELOAD_QUERY_PARAM);
  window.history.replaceState(window.history.state, document.title, currentUrl.toString());
}

function pruneExpiredReloadState(now: number): void {
  const previous = readReloadState();
  if (!previous) {
    return;
  }
  if (now - previous.at >= RELEASE_RELOAD_COOLDOWN_MS) {
    clearReloadState();
  }
}

function shouldProbeRuntimeRelease(now: number, lastCheckAt: number, minIntervalMs = RELEASE_PROBE_MIN_INTERVAL_MS): boolean {
  return now - lastCheckAt >= minIntervalMs;
}

async function probeRuntimeRelease(): Promise<void> {
  if (typeof window === 'undefined' || typeof fetch !== 'function') {
    return;
  }
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return;
  }

  const now = Date.now();
  if (!shouldProbeRuntimeRelease(now, lastProbeAt) || inflightProbe) {
    return inflightProbe ?? undefined;
  }

  lastProbeAt = now;
  inflightProbe = (async () => {
    try {
      const response = await fetch('/health', {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });
      handleRuntimeReleaseHeaders(response.headers);
    } catch {
      // Ignore transient probe failures; next focus/pageshow will retry.
    } finally {
      inflightProbe = null;
    }
  })();

  await inflightProbe;
}

export function handleRuntimeReleaseHeaders(headers: Headers): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const runtimeReleaseId = headers.get(RELEASE_ID_HEADER)?.trim() ?? '';
  const clientReleaseId = currentClientReleaseId();
  if (!runtimeReleaseId || !clientReleaseId || runtimeReleaseId === clientReleaseId) {
    return false;
  }

  const now = Date.now();
  const previous = readReloadState();
  if (
    !shouldReloadForReleaseMismatch(
      now,
      previous,
      runtimeReleaseId,
      window.navigator.onLine,
    )
  ) {
    return false;
  }

  writeReloadState({
    at: now,
    fromHref: window.location.href,
    runtimeReleaseId,
  });
  window.location.replace(buildReleaseReloadHref(window.location.href, now));
  return true;
}

export function initRuntimeReleaseGuard(): void {
  if (typeof window === 'undefined' || releaseGuardInitialized) {
    return;
  }
  releaseGuardInitialized = true;
  const now = Date.now();
  clearReleaseReloadQueryParam();
  pruneExpiredReloadState(now);

  const scheduleProbe = () => {
    void probeRuntimeRelease();
  };

  window.addEventListener('focus', scheduleProbe);
  window.addEventListener('pageshow', scheduleProbe);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        scheduleProbe();
      }
    });
  }
}

export const __buildReleaseReloadHrefForTests = buildReleaseReloadHref;
export const __shouldReloadForReleaseMismatchForTests = shouldReloadForReleaseMismatch;
export const __shouldProbeRuntimeReleaseForTests = shouldProbeRuntimeRelease;
